import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import {
  IDeviceTemplateForm,
  ITemplateData,
  deviceTemplateRules,
  modelLabelMap,
} from "../variables/deviceTemplateData";

interface DeviceTemplateFormItemProps {
  index: number;
  device: ITemplateData;
  numberOfNodes: number;
  deviceTypeOptions: string[];
  getModelsToRunForDeviceType: (type: string) => string[];
  onRemove: (index: number) => void;
  control: Control<IDeviceTemplateForm>;
  errors: FieldErrors<IDeviceTemplateForm>;
  watchedDeviceType: string;
  setValue: UseFormSetValue<IDeviceTemplateForm>;
}

const DeviceTemplateFormItem = ({
  index,
  device,
  numberOfNodes,
  deviceTypeOptions,
  getModelsToRunForDeviceType,
  onRemove,
  control,
  errors,
  watchedDeviceType,
  setValue,
}: DeviceTemplateFormItemProps): JSX.Element => {
  const getNodeOptions = (count: number): number[] =>
    Array.from({ length: count }, (_, i) => i + 1);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        borderRadius: "8px",
        padding: "1em",
      }}
    >
      <Typography sx={{ minWidth: "70px" }}>Device {index + 1}</Typography>
      <Controller
        name={`templateData.${index}.deviceType`}
        control={control}
        defaultValue={device.deviceType}
        rules={{
          required: "Device Type is required",
        }}
        render={({ field }) => (
          <TextField
            select
            label="Device Type"
            value={field.value}
            sx={{ minWidth: 180 }}
            onChange={e => {
              field.onChange(e);

              setValue(`templateData.${index}.model`, []);
            }}
            error={!!errors?.templateData?.[index]?.deviceType}
            helperText={
              errors?.templateData?.[index]?.deviceType &&
              "Device Type is required"
            }
          >
            {/* Filter for now until mapTemplateDataToDeviceInput is refactored for all devices */}
            {deviceTypeOptions
              .filter(type =>
                ["CC CH4", "CC FB4", "CC FB4 Thermal", "V1"].includes(type)
              )
              .map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
          </TextField>
        )}
      />
      <Controller
        name={`templateData.${index}.nodeNumber`}
        control={control}
        defaultValue={device.nodeNumber}
        rules={{
          required: "Node is required",
        }}
        render={({ field }) => (
          <TextField
            select
            label="Node Number"
            value={field.value}
            sx={{ minWidth: 150 }}
            onChange={field.onChange}
            error={!!errors?.templateData?.[index]?.nodeNumber}
            helperText={
              errors?.templateData?.[index]?.nodeNumber && "Node is required"
            }
          >
            {getNodeOptions(numberOfNodes).map(number => (
              <MenuItem key={number} value={number}>
                {number}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name={`templateData.${index}.deviceIp`}
        control={control}
        defaultValue={device.deviceIp}
        rules={{
          required: "This field is required",
          pattern: {
            value: deviceTemplateRules.templateData.deviceIp.pattern,
            message: "Enter a valid IP (e.g., 192.168.1.1)",
          },
        }}
        render={({ field }) => (
          <TextField
            label="Device IP"
            {...field}
            error={!!errors?.templateData?.[index]?.deviceIp}
            helperText={
              errors?.templateData?.[index]?.deviceIp &&
              "Enter a valid IP (e.g., 192.168.1.1)"
            }
          />
        )}
      />
      <Controller
        name={`templateData.${index}.model`}
        control={control}
        defaultValue={device.model || []}
        rules={{
          required: "Model is required",
        }}
        render={({ field }) => (
          <TextField
            select
            label="Model(s)"
            value={field.value}
            onChange={event =>
              field.onChange(
                (event.target.value as unknown as string[]).filter(
                  val => val.trim() !== ""
                )
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: selected =>
                (selected as string[])
                  .filter(model => model.trim() !== "")
                  .map(model => modelLabelMap[model] || model)
                  .join(", "),
            }}
            sx={{ minWidth: 220 }}
            error={!!errors?.templateData?.[index]?.model}
            helperText={
              errors?.templateData?.[index]?.model && "Model is required"
            }
          >
            {getModelsToRunForDeviceType(watchedDeviceType).map(model => (
              <MenuItem key={model} value={model}>
                <Checkbox
                  checked={field.value?.includes(model)}
                  sx={{ marginRight: 1 }}
                />
                {modelLabelMap[model] || model}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Button variant="outlined" color="error" onClick={() => onRemove(index)}>
        Remove
      </Button>
    </Box>
  );
};

export default DeviceTemplateFormItem;
